<template>
  <article v-editable="blok" class="article-wrapper">
    <component :is="pageBannerComponent" v-if="pageBanner" :blok="pageBanner">
      <template #filters>
        <component :is="filterComponent" v-if="hasFilterBar" />
      </template>
    </component>
    <PaginationTotals
      v-if="cards.length"
      :current-page="currentPage"
      :pages-total="pagesTotal"
      :total-items="totalItems"
    />
    <PaginationNoResults v-if="!loading && !cards.length" />
    <PaginationError v-else-if="!loading && error" />
    <section v-if="cards.length" class="cards-list">
      <!-- card list -->
      <component
        :is="listComponent"
        :blok="cards"
        :content-type="contentType"
        :is-filtered="isFiltered"
      />
    </section>
    <PaginationPaginate
      v-if="hasPages"
      :margin-pages="1"
      :page-range="3"
      :pages-total="pagesTotal"
      :value="currentPage"
      @on-set-page="setPage"
    />
    <!-- hr.no-pagination(v-else-if="cards.length" aria-label="Nothing more to load") -->
    <div v-if="blocks.length" class="container">
      <component
        :is="block.component"
        v-for="block in blocks"
        :key="block._uid"
        :blok="block"
      />
    </div>
    <ContentSubscribe v-if="!pageContainsSubscriptionForm" />
  </article>
</template>

<script setup lang="ts">
import type { LandingPageStoryblok } from '~/types/storyblok'
const route = useRoute()
const props = defineProps<{ blok: LandingPageStoryblok }>()
const {
  pageBanner,
  pageBannerComponent,
  blocks,
  pageContainsSubscriptionForm,
} = useStoryContent(toRefs(props))
const contentType = computed(() => route.path.replace(/\//g, ''))
const listComponent = computed(() => contentType.value + 'List')
const filterComponent = computed(() => contentType.value + 'FilterBar')
const componentType = computed(() => slugToComponentTypeName(contentType.value))
const { isFiltered, hasFilterBar, sortByQuery, searchFilters } =
  useFiltersWithIndices({ contentType, componentType })

const {
  currentPage,
  setPage,
  hasPages,
  searchPagination,
  calculateTotalPages,
  itemsPerPage,
} = await usePaginationWithIndices()
const pagesTotal = computed(() =>
  calculateTotalPages(totalItems.value, itemsPerPage.value),
)
const formatHitAsStory = (hit) => {
  return {
    ...hit,
    name: hit.title,
    published_at: hit.firstPublishedAt || hit.date,
    full_slug: hit.fullSlug || hit.url,
    content: {
      ...hit,
      ...hit.meta,
      logo: { filename: hit.logo },
      image: { filename: hit.image },
    },
  }
}

const search = async () => {
  const algoliaIndex = getAlgoliaIndex()
  const { hits, nbHits, nbSortedHits } = await algoliaIndex.search('', {
    ...searchFilters.value,
    ...searchPagination.value,
  })

  return { stories: hits.map(formatHitAsStory), total: nbSortedHits || nbHits }
}

const { data, error, refresh, status } = await useAsyncData<{
  stories: any[]
  total: number
}>(async () => await search(), {
  watch: [searchFilters, searchPagination],
  immediate: false,
})

const cards = computed(() => data.value?.stories || [])
const totalItems = computed(() => data.value?.total)
const loading = computed(() => ['idle', 'pending'].includes(status.value))

const { defaultIndex, sortNameAscIndex, sortNameDescIndex, sortDateDescIndex } =
  useAlgolia()

const getAlgoliaIndex = () => {
  switch (sortByQuery.value.sort_by) {
    case 'name:asc':
      return sortNameAscIndex
    case 'name:desc':
      return sortNameDescIndex
    case 'first_published_at:desc':
      return sortDateDescIndex
    default:
      return defaultIndex
  }
}
onBeforeMount(() => {
  if (isFiltered.value) refresh()
})
</script>
